<template>
  <CRow>
    <CCol col="12">
      <CCard>
        <CCardHeader>
          <h4>Εξαγωγή Μητρώου Πιστοποιητικών</h4>
        </CCardHeader>
        <CCardBody>
          <!-- Filters Section -->
          <CCard class="mb-4">
            <CCardHeader>
              <strong>Φίλτρα</strong>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol md="6">
                  <div class="form-group">
                    <label>Πρότυπο (ISO)</label>
                    <multiselect
                      v-model="selectedServices"
                      :options="groupedServices"
                      :multiple="true"
                      :close-on-select="false"
                      :clear-on-select="false"
                      :preserve-search="true"
                      placeholder="Επιλέξτε πρότυπα"
                      label="name"
                      track-by="id"
                      group-values="services"
                      group-label="group"
                      :group-select="true"
                    />
                  </div>
                </CCol>
              </CRow>
              <CRow>
                <CCol md="3">
                  <div class="form-group">
                    <label>Ημ/νία Επιθεώρησης Από</label>
                    <input
                      type="date"
                      class="form-control"
                      v-model="filters.date_from"
                    />
                  </div>
                </CCol>
                <CCol md="3">
                  <div class="form-group">
                    <label>Ημ/νία Επιθεώρησης Έως</label>
                    <input
                      type="date"
                      class="form-control"
                      v-model="filters.date_to"
                    />
                  </div>
                </CCol>
                <CCol md="3">
                  <div class="form-group">
                    <label>Αριθμός Υπόθεσης Από</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="filters.case_number_from"
                      placeholder="π.χ. 0010.00101"
                    />
                  </div>
                </CCol>
                <CCol md="3">
                  <div class="form-group">
                    <label>Αριθμός Υπόθεσης Έως</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="filters.case_number_to"
                      placeholder="π.χ. 0010.01000"
                    />
                  </div>
                </CCol>
              </CRow>
              <CRow>
                <CCol>
                  <CButton color="primary" @click="loadPreview" :disabled="loading">
                    <span v-if="loading">
                      <CSpinner size="sm" /> Φόρτωση...
                    </span>
                    <span v-else>Προεπισκόπηση</span>
                  </CButton>
                  <CButton color="secondary" class="ml-2" @click="clearFilters">
                    Καθαρισμός Φίλτρων
                  </CButton>
                  <CButton
                    color="success"
                    class="ml-2"
                    @click="exportToExcel"
                    :disabled="!hasData || loading"
                  >
                    Εξαγωγή σε Excel
                  </CButton>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>

          <!-- Summary -->
          <CAlert v-if="hasData" color="info" class="mb-4">
            <strong>Σύνοψη:</strong>
            {{ summary.total_records }} εγγραφές
          </CAlert>

          <!-- Preview Table -->
          <CCard v-if="hasData">
            <CCardHeader>
              <strong>Προεπισκόπηση Δεδομένων</strong>
            </CCardHeader>
            <CCardBody>
              <CDataTable
                :items="data"
                :fields="tableFields"
                :items-per-page="15"
                hover
                pagination
                table-filter
                sorter
                clickable-rows
                @row-clicked="openCase"
              />
            </CCardBody>
          </CCard>

          <!-- No Data Message -->
          <CAlert v-if="loaded && !hasData" color="warning">
            Δεν βρέθηκαν εγγραφές με τα επιλεγμένα φίλτρα.
          </CAlert>
        </CCardBody>
      </CCard>
    </CCol>
  </CRow>
</template>

<script>
import axios from 'axios'
import * as XLSX from 'xlsx'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'

const tableFields = [
  { key: 'case_number', label: 'Αριθμός Υπόθεσης', sorter: true },
  { key: 'certification_number_date', label: 'Αρ. Πιστοποιητικού / Ημ/νία', sorter: true },
  { key: 'client_name_address', label: 'Επωνυμία / Διεύθυνση Πελάτη', sorter: true, _style: 'white-space: pre-line;' },
  { key: 'vat', label: 'ΑΦΜ', sorter: true },
  { key: 'inspector', label: 'Επιθεωρητής', sorter: true },
  { key: 'case_type', label: 'Τύπος Υπόθεσης', sorter: true },
  { key: 'service_subcategory', label: 'Υποκατηγορία', sorter: true },
  { key: 'product', label: 'Προϊόν', sorter: true }
]

export default {
  name: 'ExportCertificationRegistry',
  components: {
    Multiselect
  },
  data() {
    return {
      loading: false,
      loaded: false,
      filters: {
        date_from: '',
        date_to: '',
        case_number_from: '0010.00001',
        case_number_to: '0010.00001'
      },
      services: [],
      selectedServices: [],
      data: [],
      summary: {
        total_records: 0
      },
      tableFields
    }
  },
  computed: {
    hasData() {
      return this.data && this.data.length > 0
    },
    groupedServices() {
      const groups = {}
      this.services.forEach(service => {
        const groupName = service.group || 'Χωρίς ομάδα'
        if (!groups[groupName]) {
          groups[groupName] = []
        }
        groups[groupName].push(service)
      })
      return Object.keys(groups).map(group => ({
        group: group,
        services: groups[group]
      }))
    }
  },
  methods: {
    async loadFilterOptions() {
      try {
        const response = await axios.get('/api/certification-registry-export/filter-options', {
          params: { token: localStorage.getItem('api_token') }
        })
        this.services = response.data.services || []
      } catch (error) {
        console.error('Error loading filter options:', error)
      }
    },
    async loadPreview() {
      this.loading = true
      try {
        const params = {
          token: localStorage.getItem('api_token'),
          ...this.filters
        }
        // Handle service_ids from multiselect
        if (this.selectedServices && this.selectedServices.length > 0) {
          params.service_ids = this.selectedServices.map(s => s.id).join(',')
        }
        // Remove empty filters
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null || (Array.isArray(params[key]) && params[key].length === 0)) {
            delete params[key]
          }
        })

        const response = await axios.get('/api/certification-registry-export/preview', { params })
        this.data = response.data.data || []
        this.summary = response.data.summary || { total_records: 0 }
        this.loaded = true
      } catch (error) {
        console.error('Error loading preview:', error)
        if (error.response && error.response.status === 401) {
          this.$router.push({ path: '/login' })
        }
      } finally {
        this.loading = false
      }
    },
    clearFilters() {
      this.filters = {
        date_from: '',
        date_to: '',
        case_number_from: '0010.00001',
        case_number_to: '0010.00001'
      }
      this.selectedServices = []
    },
    openCase(item) {
      const routeData = this.$router.resolve({ path: `/cases/${item.case_id}` })
      window.open(routeData.href, '_blank')
    },
    exportToExcel() {
      if (!this.hasData) return

      // Create workbook
      const wb = XLSX.utils.book_new()

      // Headers
      const headers = [
        'Αριθμός Υπόθεσης',
        'Αρ. Πιστοποιητικού / Ημ/νία',
        'Επωνυμία / Διεύθυνση Πελάτη',
        'ΑΦΜ',
        'Επιθεωρητής',
        'Τύπος Υπόθεσης',
        'Υποκατηγορία',
        'Προϊόν'
      ]

      // Data rows
      const rows = this.data.map(row => [
        row.case_number,
        row.certification_number_date,
        row.client_name_address,
        row.vat,
        row.inspector,
        row.case_type,
        row.service_subcategory,
        row.product
      ])

      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])

      // Set wrap text for client name/address column (column C, index 2)
      const range = XLSX.utils.decode_range(ws['!ref'])
      for (let row = 1; row <= range.e.r; row++) {
        const cellRef = XLSX.utils.encode_cell({ r: row, c: 2 })
        if (ws[cellRef]) {
          if (!ws[cellRef].s) ws[cellRef].s = {}
          ws[cellRef].s.alignment = { wrapText: true }
        }
      }

      // Set column widths
      ws['!cols'] = [
        { wch: 18 }, // Αριθμός Υπόθεσης
        { wch: 25 }, // Αρ. Πιστοποιητικού / Ημ/νία
        { wch: 40 }, // Επωνυμία / Διεύθυνση Πελάτη
        { wch: 12 }, // ΑΦΜ
        { wch: 20 }, // Επιθεωρητής
        { wch: 18 }, // Τύπος Υπόθεσης
        { wch: 20 }, // Υποκατηγορία
        { wch: 30 }  // Προϊόν
      ]

      XLSX.utils.book_append_sheet(wb, ws, 'Μητρώο Πιστοποιητικών')

      // Generate filename with date
      const today = new Date().toISOString().slice(0, 10)
      const filename = `certification_registry_${today}.xlsx`

      // Download
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' })

      function s2ab(s) {
        const buf = new ArrayBuffer(s.length)
        const view = new Uint8Array(buf)
        for (let i = 0; i < s.length; i++) {
          view[i] = s.charCodeAt(i) & 0xFF
        }
        return buf
      }

      const blob = new Blob([s2ab(wbout)], { type: 'application/octet-stream' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    }
  },
  mounted() {
    this.loadFilterOptions()
  }
}
</script>

<style scoped>
.form-group {
  margin-bottom: 1rem;
}
.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}
.ml-2 {
  margin-left: 0.5rem;
}
.mb-4 {
  margin-bottom: 1.5rem;
}
/deep/ table tbody tr {
  cursor: pointer;
}
</style>
